import * as React from "react";
import { Icon, ComponentType, Action } from "../frame/index.js";
import * as cls from "./WidgetFrame.module.scss";
export { WidgetFrame };
const cType = new ComponentType("WidgetFrame", "widget_", cls);
/**
 * Фрейм виджета Личной студии, включающий заголовок
 * и обрамление основного содержимого.
 * @summary Фрейм виджета Личной студии.
 * @param {Object} props - Атрибуты элемента.
 * @param {string} [props.icon] - Имя иконки.
 * @param {React.ReactNode} [props.title] - Заголовок секции.
 * @param {"default"|"primary"|"danger"|"green"|"blue"|"yellow"|"burgundy"|"none"} [props.type=default] - Тип виджета.
 * @param {string} [props.className] - Дополнительные классы CSS.
 * @param {Action[]} [props.menu] - Элементы меню.
 * @param {JSX.Element} props.children - Содержимое.
 * @returns {JSX.Element} Элемент JSX.
 * @example
import { WidgetFrame } from "../personal-studio-components/studio/index.js";
<WidgetFrame icon="question" title="Заголовок виджета">
    Содержимое
</WidgetFrame>
 * @author Алексей Васьковский < avaskovskiy@umney.ru>
 */
function WidgetFrame(
  {
    icon = "question",
    title = "WidgetFrame.title",
    type = "default",
    className,
    menu = [],
    children
  }) {
  const fullClassName = cType.getFullClassName(type, className);
  let header = null;
  if ("none" != type) {
    const widgetMenu = "default" === type ?
      <DefaultWidgetMenu menu={menu} /> :
      <CompactWidgetMenu menu={menu} />;
    header = renderWidgetHeader(icon, title, widgetMenu);
  }
  let style = {};
  return renderWidgetFrame(fullClassName, style, header, children);
}
/** 
 * Тип фрейма виджета.
 * @enum {string}
 * @inner
 */
WidgetFrame.Type =
{
  /** По умолчанию. Единственный виджет на всю страницу. */
  DEFAULT: "default",
  /** Основной с инвертированными цветами. */
  PRIMARY: "primary",
  /** Виджет с красной иконкой и сообщением об ошибке . */
  DANGER: "danger",
  /** Виджет с зелёной иконкой. */
  GREEN: "green",
  /** Виджет с синей иконкой. */
  BLUE: "blue",
  /** Виджет с жёлтой иконкой. */
  YELLOW: "yellow",
  /** Виджет с пурпурной иконкой. */
  MAGENTA: "burgundy",
  /** Виджет с бордовой иконкой. */
  BURGUNDY: "burgundy",
  /** Виджет без заголовка. */
  NONE: "none"
};

// Разметка WidgetFrame
function renderWidgetFrame(fullClassName, style, header, children) {
  return (
    <section className={fullClassName} style={style}>
      {header}
      {children}
    </section>
  );
}

// Разметка заголовка WidgetFrame
function renderWidgetHeader(icon, title, widgetMenu) {
  return (
    <h2 className={cls.widget_header}>
      <span className={cls.widget_icon}>
        <Icon name={icon} />
      </span>
      <span className={cls.widget_title}>
        {title}
      </span>
      {widgetMenu}
    </h2>
  );
}

// Меню WidgetFrame по умолчанию (type=default)
function DefaultWidgetMenu({ menu }) {
  if (!menu || !menu.length) return null;
  const actionList = [];
  for (const menuItem of menu) {
    const action = new Action(menuItem);
    actionList.push(
      <DefaultWidgetMenuItem key={action.name} action={action} />);
  }
  return (
    <DefaultWidgetMenuContainer>{actionList}</DefaultWidgetMenuContainer>
  );
}

// Меню WidgetFrame на главной странице (type!=default)
function CompactWidgetMenu({ menu }) {
  const [open, setOpen] = React.useState(false);
  if (!menu || !menu.length) return null;
  const actionList = [];
  for (const menuItem of menu) {
    const action = new Action(menuItem);
    actionList.push(
      <CompactWidgetMenuItem
        key={action.name}
        action={action}
        onToggle={onToggle} />);
  }
  return (
    <CompactWidgetMenuContainer open={open} onToggle={onToggle}>
      {actionList}
    </CompactWidgetMenuContainer>
  );
  function onToggle(event) {
    event.preventDefault();
    setOpen(!open);
  }
}

// Контейнер меню по умолчанию
function DefaultWidgetMenuContainer({ children }) {
  return <ul className={cls.widget_menu}>{children}</ul>;
}

// Элемент меню по умолчанию
function DefaultWidgetMenuItem({ action }) {
  return (
    <li role="none">
      <a {...action.getAnchorAttributes()}>
        <Icon name={action.icon} />
        {" " + action.name}
      </a>
    </li>
  );
}

// Контейнер компактного меню
function CompactWidgetMenuContainer({ open, onToggle, children }) {
  const dropdown = open ?
    <div className={cls.widget_menu_dropdown} onMouseLeave={onToggle}>
      {children}
    </div> :
    null;
  return (
    <div className={cls.widget_menu_container}>
      <a className={cls.widget_menu_icon} onClick={onToggle}>
        <Icon name="ellipsis" />
      </a>
      {dropdown}
    </div>
  );
}

// Элемент компактного меню
function CompactWidgetMenuItem({ action, onToggle }) {
  return <a {...action.getAnchorAttributes()} onClick={onClick}>
    {action.name}
  </a>;
  function onClick(event) {
    if (action.onClick) action.onClick(event);
    onToggle(event);
  }
}
